import { PowerUpData, Position } from '../types/GameTypes';

export class PowerUp {
  public id: string;
  public type: 'boost' | 'shield' | 'health' | 'mega-jump' | 'gravity-flip';
  public position: Position;
  public emoji!: string;
  public className!: string;
  public duration!: number;
  public respawnTime!: number;
  public element: HTMLElement | null = null;
  
  private spawnTime: number;
  private isCollected: boolean = false;

  constructor(id: string, type: 'boost' | 'shield' | 'health' | 'mega-jump' | 'gravity-flip', position: Position) {
    this.id = id;
    this.type = type;
    this.position = { ...position };
    this.spawnTime = Date.now();
    
    this.setupPowerUpProperties();
  }

  private setupPowerUpProperties(): void {
    switch (this.type) {
      case 'boost':
        this.emoji = '⚡';
        this.className = 'boost';
        this.duration = 0; // Instant effect
        this.respawnTime = 8000; // 8 seconds
        break;
        
      case 'shield':
        this.emoji = '🛡️';
        this.className = 'shield';
        this.duration = 5000; // 5 seconds
        this.respawnTime = 12000; // 12 seconds
        break;
        
      case 'health':
        this.emoji = '❤️';
        this.className = 'health';
        this.duration = 0; // Instant effect
        this.respawnTime = 15000; // 15 seconds (rare)
        break;
        
      case 'mega-jump':
        this.emoji = '🚀';
        this.className = 'mega-jump';
        this.duration = 0; // Instant effect
        this.respawnTime = 10000; // 10 seconds
        break;
        
      case 'gravity-flip':
        this.emoji = '🔄';
        this.className = 'gravity-flip';
        this.duration = 3000; // 3 seconds
        this.respawnTime = 20000; // 20 seconds (very rare)
        break;
    }
  }

  static createRandom(areaWidth: number, areaHeight: number, existingPowerups: PowerUp[] = []): PowerUp {
    // Weighted selection for powerup types
    const weightedTypes: { type: PowerUp['type']; weight: number }[] = [
      { type: 'boost', weight: 30 },
      { type: 'shield', weight: 25 },
      { type: 'mega-jump', weight: 20 },
      { type: 'health', weight: 15 },
      { type: 'gravity-flip', weight: 10 }
    ];
    
    // Filter out types that are already present (prevent duplicates of rare powerups)
    const availableTypes = weightedTypes.filter(({ type }) => {
      if (type === 'health' || type === 'gravity-flip') {
        return !existingPowerups.some(p => p.type === type);
      }
      return true;
    });
    
    // Select type based on weights
    const totalWeight = availableTypes.reduce((sum, { weight }) => sum + weight, 0);
    let random = Math.random() * totalWeight;
    
    let selectedType: PowerUp['type'] = 'boost';
    for (const { type, weight } of availableTypes) {
      random -= weight;
      if (random <= 0) {
        selectedType = type;
        break;
      }
    }
    
    // Smart positioning - avoid edges and existing powerups
    const position = PowerUp.findGoodSpawnPosition(areaWidth, areaHeight, existingPowerups);
    
    return new PowerUp(Date.now().toString() + Math.random(), selectedType, position);
  }

  private static findGoodSpawnPosition(areaWidth: number, areaHeight: number, existingPowerups: PowerUp[]): Position {
    const margin = 80;
    const minDistance = 100;
    let attempts = 0;
    const maxAttempts = 20;
    
    while (attempts < maxAttempts) {
      const position = {
        x: margin + Math.random() * (areaWidth - 2 * margin),
        y: margin + Math.random() * (areaHeight - 2 * margin)
      };
      
      // Check distance from existing powerups
      const tooClose = existingPowerups.some(powerup => {
        const distance = Math.sqrt(
          Math.pow(position.x - powerup.position.x, 2) + 
          Math.pow(position.y - powerup.position.y, 2)
        );
        return distance < minDistance;
      });
      
      if (!tooClose) {
        return position;
      }
      
      attempts++;
    }
    
    // Fallback to center if no good position found
    return { x: areaWidth / 2, y: areaHeight / 2 };
  }

  createDOMElement(): HTMLElement {
    const element = document.createElement('div');
    element.className = `powerup ${this.className}`;
    element.style.left = (this.position.x - 22.5) + 'px';
    element.style.top = (this.position.y - 22.5) + 'px';
    element.textContent = this.emoji;
    
    // Add rarity indicator
    if (this.type === 'health' || this.type === 'gravity-flip') {
      element.classList.add('rare');
    }
    
    // Add spawn animation
    element.style.transform = 'scale(0)';
    element.style.opacity = '0';
    
    setTimeout(() => {
      element.style.transition = 'all 0.3s ease';
      element.style.transform = 'scale(1)';
      element.style.opacity = '1';
    }, 50);
    
    this.element = element;
    return element;
  }

  update(): void {
    if (!this.element || this.isCollected) return;
    
    // Pulsing effect based on age
    const age = Date.now() - this.spawnTime;
    const pulseSpeed = 1000 + (age / 10); // Pulses faster as it gets older
    const scale = 1 + Math.sin(age / pulseSpeed) * 0.1;
    
    this.element.style.transform = `scale(${scale})`;
    
    // Despawn after 30 seconds
    if (age > 30000) {
      this.destroy();
    }
  }

  collect(): boolean {
    if (this.isCollected) return false;
    
    this.isCollected = true;
    
    // Collection animation
    if (this.element) {
      this.element.style.transition = 'all 0.2s ease';
      this.element.style.transform = 'scale(1.5)';
      this.element.style.opacity = '0';
      
      // Particle effect
      this.createCollectionParticles();
      
      setTimeout(() => {
        this.destroy();
      }, 200);
    }
    
    return true;
  }

  private createCollectionParticles(): void {
    if (!this.element) return;
    
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'powerup-particle';
      particle.style.position = 'absolute';
      particle.style.left = this.position.x + 'px';
      particle.style.top = this.position.y + 'px';
      particle.style.width = '4px';
      particle.style.height = '4px';
      particle.style.borderRadius = '50%';
      particle.style.background = this.getParticleColor();
      particle.style.pointerEvents = 'none';
      particle.style.zIndex = '1001';
      
      // Random direction
      const angle = (i / particleCount) * Math.PI * 2;
      const speed = 50 + Math.random() * 30;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      
      this.element.parentNode?.appendChild(particle);
      
      // Animate particle
      particle.style.transition = 'all 0.5s ease-out';
      setTimeout(() => {
        particle.style.left = (this.position.x + vx) + 'px';
        particle.style.top = (this.position.y + vy) + 'px';
        particle.style.opacity = '0';
      }, 10);
      
      // Remove particle
      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 500);
    }
  }

  private getParticleColor(): string {
    switch (this.type) {
      case 'boost': return '#FFD700';
      case 'shield': return '#00FFFF';
      case 'health': return '#FF69B4';
      case 'mega-jump': return '#FF4500';
      case 'gravity-flip': return '#9370DB';
      default: return '#FFFFFF';
    }
  }

  distanceFrom(position: Position): number {
    return Math.sqrt(
      Math.pow(this.position.x - position.x, 2) + 
      Math.pow(this.position.y - position.y, 2)
    );
  }

  getDescription(): string {
    switch (this.type) {
      case 'boost': return 'Gives instant speed boost';
      case 'shield': return 'Protects from damage for 5 seconds';
      case 'health': return 'Restores 30 health points';
      case 'mega-jump': return 'Gives one super powerful jump';
      case 'gravity-flip': return 'Flips gravity for 3 seconds';
      default: return 'Unknown powerup';
    }
  }

  getRarity(): 'common' | 'uncommon' | 'rare' | 'legendary' {
    switch (this.type) {
      case 'boost': return 'common';
      case 'mega-jump': return 'common';
      case 'shield': return 'uncommon';
      case 'health': return 'rare';
      case 'gravity-flip': return 'legendary';
      default: return 'common';
    }
  }

  destroy(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  toData(): PowerUpData {
    return {
      id: this.id,
      type: this.type,
      position: { ...this.position },
      emoji: this.emoji,
      className: this.className,
      duration: this.duration,
      respawnTime: this.respawnTime
    };
  }
}